<template>
  <div ref="container">
    <svg ref="tree" :height="height" :width="width">
      <g ref="main" :transform="`translate(${padding[3]}, ${padding[0]})`" class="main"></g>
    </svg>
    <div ref="tooltip"></div>
  </div>
</template>

<script>

import * as d3 from "d3"
import * as tooltip from "./Tooltip"

export default {
  props: {
    height: { type: Number },
    width: { type: Number },
    padding: { type: Array },
    color_map: { },
    is_color_dark: { },
    data: { },
  },
  data: () => ({
    mouseover_data: null,
  }),
  computed: {
    innerHeight() {
      return this.height - (this.padding[0] + this.padding[2])
    },
    innerWidth() {
      return this.width - (this.padding[1] + this.padding[3])
    }
  },
  mounted() {
    const tooltipDiv = d3.select(this.$refs.tooltip);
    tooltip.reinit(tooltipDiv);
    this.draw(this.data);
  },
  watch: {
   $props: {
      handler() {
        this.draw(this.data);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    draw(data) {
      if(this.innerWidth <= 0 || this.innerHeight <= 0) 
        return; //Nothing to do

      let svg = d3.select(this.$refs.tree);

      if(data == null) {
        svg.select(".main").selectAll("g").remove();
        return;
      }

      const tooltipDiv = d3.select(this.$refs.tooltip);

      //Update the tooltip in a progressive environment
      if(this.mouseover_dataid != null) {
        let finddata_rec = (node) => {
          if(node.type_combination == this.mouseover_dataid)
            return node;
          for(let child of node.children) {
            let res = finddata_rec(child);
            if(res != null)
              return res;
          }
          return null;
        };

        var mouseover_data = finddata_rec(data);
        if(mouseover_data != null)
          tooltipDiv.html(`${mouseover_data.name}: ${mouseover_data.nb_occurences.toLocaleString()}`);
      }

      var root = d3.partition().size([this.innerWidth,this.innerHeight])(d3.hierarchy(data).each((n) => {
        n.value = n.data.nb_occurences
        n.depth--;
      }));
      var nodes = root.descendants();
      var max_depth = nodes.reduce((a, b) => Math.max(a, b.depth), 0);

      var get_text = (d) => {
       if(d.x1 - d.x0 < 5)
          return '';
        else if (d.data.name.length > 40/(max_depth+2))
          return '...';
        return d.data.name;
      };

      nodes = nodes.filter((n) => n.depth >= 0);

      svg.select(".main").selectAll("g")
        .data(nodes, (d) => d.data.name)
         .join(
           enter => {
             var g = enter.append("g")
               .on("mouseout", () => {
                 this.mouseover_dataid = null;
                 tooltip.mouseout(tooltipDiv);
               })
               .on("mouseover", (event, d) => {
                 this.mouseover_dataid = d.data.type_combination;
                 tooltip.mouseover(tooltipDiv, event, `${d.data.name}: ${d.value.toLocaleString()}`);
               });
             
             g.append("rect")
               .attr("width",  d => d.x1 - d.x0)
               .attr("height", d => d.y1 - d.y0)
               .attr("x", d => d.x0)
               .attr("y", d => d.y0)
               .attr("fill", (d) => this.color_map[d.data.name] || this.color_map.default);

             g.append("text")
               .text((d) => get_text(d))
               .attr("text-anchor", "middle")
               .attr("font-size", "0.8em")
               .attr("fill", (d) => this.is_color_dark[d.data.name] || this.is_color_dark.default ? "white" : "black")
               .attr("transform", d => `translate(${d.x0+(d.x1-d.x0)/2},${d.y0+(d.y1-d.y0)/2})rotate(${(d.x1 - d.x0) < (d.y1 - d.y0) ? 90 : 0})`);
           }
           ,
           update => {
             update.select("rect")
               .attr("width",  d => d.x1 - d.x0)
               .attr("height", d => d.y1 - d.y0)
               .attr("x", d => d.x0)
               .attr("y", d => d.y0);
             update.select("text")
               .text((d) => get_text(d))
               .attr("transform", d => `translate(${d.x0+(d.x1-d.x0)/2},${d.y0+(d.y1-d.y0)/2})rotate(${(d.x1 - d.x0) < (d.y1 - d.y0) ? 90 : 0})`);
           }
           ,
           exit => exit.remove()
           ,
      );
    }
  }
}

</script>
